<script>
  import { useRouter } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Pagination from '../components/Pagination.svelte';
  import EmptyState from '../components/EmptyState.svelte';

  const router = useRouter();

  let loading = $state(true);
  let error = $state(null);
  let items = $state([]);
  let pagination = $state(null);
  let page = $state(1);
  let unlinkedOnly = $state(false);

  async function load() {
    loading = true;
    error = null;
    try {
      const result = await api.listConversations({
        page, limit: 25,
        tenant_id: authStore.tenantId ?? undefined,
        unlinked_only: unlinkedOnly || undefined,
      });
      items = result.items;
      pagination = result.pagination;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { page; unlinkedOnly; load(); });

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  const columns = [
    { key: 'guest_name', label: 'Guest', render: (r) => r.guest_name ?? 'Unknown guest' },
    {
      key: 'property_name', label: 'Property',
      render: (r) => r.property_name ?? (r.is_unlinked ? 'Unlinked' : '—'),
    },
    { key: 'channel_name', label: 'Channel', render: (r) => r.channel_name ?? '—' },
    { key: 'message_count', label: 'Messages', align: 'right' },
    { key: 'last_message_at', label: 'Last activity', render: (r) => fmt(r.last_message_at) },
  ];
</script>

<PageHeader title="Conversations" subtitle="Guest message threads for your properties.">
  {#snippet children()}
    {#if authStore.canViewUnlinked}
      <label class="flex items-center gap-2 text-xs p-muted">
        <input type="checkbox" bind:checked={unlinkedOnly} />
        Unlinked only
      </label>
    {/if}
  {/snippet}
</PageHeader>

{#if authStore.canViewUnlinked && unlinkedOnly}
  <p class="mb-4 rounded-lg px-3 py-2 text-xs"
     style="background-color: var(--p-warning-bg); color: var(--p-warning)">
    <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
    These threads have no resolved property. The assistant cannot scope property knowledge for
    them, so replies can draw on the wrong property's facts. Link them in Hostaway.
  </p>
{/if}

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if !loading && !items.length}
  <EmptyState icon="fa-comments" title="No conversations yet"
              message="Guest message threads appear here once they sync from Hostaway." />
{:else}
  <DataTable {columns} rows={items} {loading}
             onRowClick={(row) => router.push({ name: 'conversation-detail', params: { id: row.id } })} />
  <Pagination {pagination} onPage={(p) => (page = p)} />
{/if}
